import { FloorplanPlacement } from '../types';

/**
 * How a placement is drawn and described.
 *
 * `unavailable` rather than `booked` on purpose: the endpoint derives `isAvailable` from the day's
 * precalculated time ranges, so false covers the slot being taken, the desk being outside its
 * booking window, or the time ranges missing altogether. Calling all of that "Booked" would state
 * something we do not know.
 *
 * `mine` and `team` come from the Coworker Visibility fields of the same response (`isMine`,
 * `booker`). `mine` is config-independent — a user always recognises their own booking (CV-FP-06) —
 * and wins over availability: a desk the user holds later today is still theirs on the map. `team`
 * is a coworker's booking whose identity the building reveals, drawn only while the map is filtered
 * to a coworker group (focus mode) — unfiltered, a coworker's desk reads like any other taken desk,
 * `unavailable`; it only ever describes an unavailable desk (a free desk with a coworker booked later that day stays green — what the user
 * can do with it matters more than who else uses it), and it is shown only when the coworker layer
 * is on — the server already strips identity, this keeps a stale cache from leaking it either.
 */
export type PlacementStatus = 'available' | 'mine' | 'team' | 'unavailable';

interface PlacementStatusOptions {
  /** `useCoworkerVisibility().effectiveName` — false hides every coworker identity on the map. */
  showCoworkerIdentity?: boolean;
  /**
   * Focus mode (§C3): the members of the active coworker group — setting it is what filters the map
   * to coworkers; without it no desk reads as `team`. When set, only THEIR desks read as `team`;
   * every other coworker's desk is just `unavailable` — purple is reserved for the group.
   *
   * Membership is judged by `booker.userUuid`, which the BE sends together with the name and the
   * initials (all three behind `coworker_show_name`). A booker WITHOUT a uuid cannot be shown to be in
   * the group, so it reads as plain `unavailable`: purple means "one of <group>", and a desk that might
   * belong to someone else must not claim it.
   */
  focusGroupMemberUuids?: ReadonlySet<string> | null;
}

export const getPlacementStatus = (
  { availability, occupancy }: FloorplanPlacement,
  { showCoworkerIdentity = false, focusGroupMemberUuids = null }: PlacementStatusOptions = {},
): PlacementStatus => {
  if (occupancy?.isMine) {
    return 'mine';
  }

  if (availability.isAvailable) {
    return 'available';
  }

  const booker = showCoworkerIdentity ? occupancy?.booker : null;

  // Coworkers are drawn only when the map is filtered to them.
  if (!booker || !focusGroupMemberUuids) {
    return 'unavailable';
  }

  // Only a booker known to be in the group is one of them.
  return booker.userUuid && focusGroupMemberUuids.has(booker.userUuid) ? 'team' : 'unavailable';
};

/** Bookable now — unchanged by the coworker layer: `team` and `unavailable` are both not bookable. */
export const isBookable = (placement: FloorplanPlacement): boolean => placement.availability.isAvailable;

/** Message id describing the status to the user — the same wording the legend uses. */
export const PLACEMENT_STATUS_LABEL_ID: Record<PlacementStatus, string> = {
  available: 'available',
  mine: 'yourBooking',
  team: 'coworker',
  unavailable: 'unavailable',
};
